import React, { useState, useEffect } from "react";
import {
  Send,
  Save,
  RefreshCw,
  AlertTriangle,
  Check,
  Building,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { facilitiesApi } from "../../utils/api";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

const EMPTY_CONFIG = {
  group_id: "",
  respond_to_messages: true,
  daily_message_limit: 50,
};

const TelegramBotConfig = () => {
  const { token } = useAuth();
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState("");
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    facilitiesApi.getAll().then((data) => setFacilities(data || []));
  }, []);

  useEffect(() => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    fetch(`${AUTH_URL}/telegram-config?facility_id=${facilityId}`, { headers })
      .then((r) => r.json())
      .then((data) => {
        setConfig({ ...EMPTY_CONFIG, ...data });
      })
      .catch(() => setError("Failed to load config"))
      .finally(() => setLoading(false));
  }, [facilityId]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = await fetch(`${AUTH_URL}/telegram-config`, {
        method: "POST",
        headers,
        body: JSON.stringify({ facility_id: facilityId, ...config }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Send className="w-5 h-5 text-blue-600" />
        <h2 className="text-xl font-bold text-slate-800">
          Telegram Bot Config
        </h2>
      </div>

      {/* Facility selector */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-3">
          <Building className="w-4 h-4 text-slate-500" />
          <label className="text-sm font-semibold text-slate-700">
            Select Facility
          </label>
        </div>
        <select
          value={facilityId}
          onChange={(e) => {
            setFacilityId(e.target.value);
            setConfig(EMPTY_CONFIG);
          }}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
        >
          <option value="">— choose a facility —</option>
          {facilities.map((f) => (
            <option key={f.facility_id} value={f.facility_id}>
              {f.display_name || f.facility_name || f.facility_id}
            </option>
          ))}
        </select>
      </div>

      {facilityId && (
        <form onSubmit={handleSave}>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 p-4">
              <RefreshCw className="w-4 h-4 animate-spin" /> Loading config…
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  {error}
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Telegram Group ID
                </label>
                <input
                  type="text"
                  value={config.group_id}
                  onChange={(e) =>
                    setConfig({ ...config, group_id: e.target.value })
                  }
                  placeholder="-1001234567890"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 outline-none"
                />
                <p className="text-xs text-slate-400 mt-1">
                  The numeric ID of the Telegram group (negative for
                  supergroups).
                </p>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    Respond to messages
                  </p>
                  <p className="text-xs text-slate-500">
                    When enabled, the bot replies in the group.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setConfig({
                      ...config,
                      respond_to_messages: !config.respond_to_messages,
                    })
                  }
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    config.respond_to_messages ? "bg-blue-600" : "bg-slate-300"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      config.respond_to_messages
                        ? "translate-x-6"
                        : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Daily Message Limit
                </label>
                <input
                  type="number"
                  min={1}
                  max={10000}
                  value={config.daily_message_limit}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      daily_message_limit: parseInt(e.target.value) || 1,
                    })
                  }
                  className="w-40 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Max bot messages per day across the group.
                </p>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : saved ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {saving ? "Saving…" : saved ? "Saved" : "Save Config"}
                </button>
              </div>
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default TelegramBotConfig;
